export type ClassName = string | null | undefined
export type ClassNameRecord = Record<string, boolean | null | undefined>
export type ClassNames = ClassName | ClassName[] | ClassNameRecord

/**
 * Get translation by key
 * @param {string} key The key to access the language object.
 * @param {object} language The language object.
 * @param {string} [fallback=''] The fallback value if the key don't exist.
 * @returns The translation for the given key.
 */
export const getTranslation = (
  key: string,
  language: object,
  fallback = ''
): string => {
  return key
    .split('.')
    .reduce((p: any, c: any) => (p && p[c]) || fallback, language)
}

const truthyNames = (value: ClassName | ClassNameRecord) =>
  (value instanceof Object
    ? Object.entries(value)
        .filter(([, value]) => !!value)
        .map(([key]) => key)
    : [value]
  ).filter(Boolean)

/**
 * Generates a `className` based on the specified values.
 *
 * Supports multiple types, see example.
 *
 * @example
 * const styles = { foo: 'foo-1g4k53' };
 * const globalClasses = ['global-1', 'global-2'];
 * const className = 'className';
 * const disabled = true;
 * const invalid = false;
 *
 * classy(styles.foo, globalClasses, className, { disabled, invalid });
 * // 'foo-1g4k53 global-1 global-2 className disabled'
 */
export const classy = (...classNames: ClassNames[]) =>
  classNames.flat(10).flatMap(truthyNames).join(' ')
